import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Building2, Calculator, DollarSign, FileCheck, ArrowRight } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";

export default function SCorpPage({ user }) {
  return (
    <DashboardLayout user={user}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold text-gray-900">S-Corp Election</h1>
          <Badge className="bg-[#e0e7ff] text-[#4f46e5]">Beta</Badge>
        </div>

        {/* Main Card */}
        <Card className="border border-gray-200">
          <CardContent className="py-16">
            <div className="text-center max-w-lg mx-auto">
              <div className="w-20 h-20 bg-[#fef3c7] rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Building2 className="w-10 h-10 text-[#d97706]" />
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">
                Save on Self-Employment Taxes
              </h2>
              <p className="text-gray-500 mb-8">
                Electing S-Corp status can save you thousands in self-employment taxes. 
                We'll help you determine if it's right for your business and handle the filing.
              </p>
              <div className="flex items-center justify-center gap-3">
                <Button className="bg-[#2563eb] hover:bg-[#1d4ed8]" data-testid="check-eligibility-btn">
                  Check Eligibility
                </Button>
                <Button variant="outline" className="gap-2">
                  Learn More <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Benefits */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Benefits of S-Corp Election</h2>
          <div className="grid grid-cols-3 gap-4">
            <Card className="border border-gray-200">
              <CardContent className="p-5">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="font-medium text-gray-900 mb-2">Tax Savings</h3>
                <p className="text-sm text-gray-500">
                  Potentially save 15.3% self-employment tax on a portion of your income.
                </p>
              </CardContent>
            </Card>
            <Card className="border border-gray-200">
              <CardContent className="p-5">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Calculator className="w-5 h-5 text-[#2563eb]" />
                </div>
                <h3 className="font-medium text-gray-900 mb-2">Salary + Distributions</h3>
                <p className="text-sm text-gray-500">
                  Split income between salary and distributions for optimal tax treatment.
                </p>
              </CardContent>
            </Card>
            <Card className="border border-gray-200">
              <CardContent className="p-5">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                  <FileCheck className="w-5 h-5 text-purple-600" />
                </div>
                <h3 className="font-medium text-gray-900 mb-2">Full Compliance</h3>
                <p className="text-sm text-gray-500">
                  We handle all IRS filings and ensure you stay compliant year-round.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Calculator Preview */}
        <Card className="border border-gray-200 bg-gray-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Estimate Your Savings</h3>
                <p className="text-sm text-gray-500">
                  See how much you could save with S-Corp status based on your income.
                </p>
              </div>
              <Button variant="outline" className="gap-2">
                <Calculator className="w-4 h-4" />
                Open Calculator
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
